import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";
import { ArrowRightIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import driverIcon from "@/assets/images/icons/Motorista-theme.svg";
import truckIcon from "@/assets/images/icons/truck-theme.svg";
import { CompanyApi } from "@/domain";
import { MESSAGE } from "@/lib/zod/messages";
import api from "@/services/api";
import { isSuccessRequest } from "@/utils";
import { cva } from "@/utils/cva.config";

import { Input } from "../Input";

import "dayjs/locale/pt-br";
dayjs.extend(utc);
dayjs.extend(timezone);

const generate4DigitToken = () => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};
const generatedToken = generate4DigitToken();

type Schema = z.infer<typeof schema>;
const schema = z
  .object({
    code: z
      .string({ required_error: MESSAGE.GLOBAL.REQUIRED })
      .nonempty({ message: MESSAGE.GLOBAL.NOT_EMPTY })
      .length(4, { message: MESSAGE.GLOBAL.LENGTH(4) })
  })
  .superRefine((val, ctx) => {
    if (val.code !== generatedToken) {
      ctx.addIssue({
        path: ["code"],
        code: z.ZodIssueCode.custom,
        message: "O código não confere."
      });
    }
  });

type TransferCompanyProps = {
  submit?: () => void;
  onAccept?: () => void;
  onDimiss?: () => void;
  company: CompanyApi;
  altCompany: CompanyApi;
  queueDate: string;
  driver: {
    id: string;
  };
};
export function TransferCompany({ context, id, innerProps: { submit, onAccept, onDimiss, queueDate, company, altCompany, driver } }: ContextModalProps<TransferCompanyProps>) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: ""
    },
    mode: "onBlur"
  });

  const dismiss = () => {
    context.closeModal(id);
    onDimiss?.();
  };

  const onSubmit: SubmitHandler<Schema> = async (_data) => {
    try {
      const response = await api.post("driver/save", {
        id: driver.id,
        company: {
          id: company.id
        },
        override: true
      });
      onAccept?.();
      context.closeModal(id);

      if (isSuccessRequest(response.status)) {
        submit?.();
        reset();
      } else {
        throw new Error();
      }
    } catch (_error) {
      toast.error("Usuário ou senha inválido");
    }
  };

  const dateQueueFormat = dayjs(queueDate).tz("America/Sao_Paulo").locale("pt-br").format("DD/MM/YYYY HH:mm").split(" ");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 pt-4">
      <div className="flex flex-col items-center justify-center gap-4 text-center font-bold uppercase *:leading-none">
        <h2 className="text-md font-medium">
          Você possui cadastro na empresa <strong>{altCompany?.company ?? "-"}</strong>, deseja transferir para empresa <strong>{company?.name ?? "-"}</strong>?
        </h2>

        <div className="xxs:flex-row flex flex-col items-center justify-center gap-2 md:gap-3">
          <div className={logo()}>
            <img src={`${import.meta.env.VITE_APP_API_URL}${altCompany?.logo}`} alt={altCompany?.company} />
          </div>

          <div className="flex flex-col items-center justify-center gap-x-[inherit]">
            <div className="flex items-center gap-[inherit]">
              <img src={driverIcon} className={icon()} />
              <img src={truckIcon} className={icon()} />
            </div>

            <ArrowRightIcon weight="bold" size={32} className="text-gray-600" />
          </div>

          <div className={logo()}>
            <img src={`${import.meta.env.VITE_APP_API_URL}${company?.logo}`} alt={company?.name} />
          </div>
        </div>

        <p className="text-sm text-gray-400">
          Ao efetuar a mudança, <span>motorista e veiculo são automaticamente desassociados da fila da empresa atual e devem reiniciar o processo na nova empresa.</span>
        </p>
      </div>

      {queueDate && (
        <p className="text-center leading-snug font-medium text-red-500">
          Sua posição na fila <strong>{altCompany?.company}</strong> com previsão para
          <br />
          <strong>
            {dateQueueFormat[0]} ás {dateQueueFormat[1]}
          </strong>
          <br />
          será cancelada automaticamente
        </p>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex flex-col items-center gap-[inherit]">
          <p className="text-primary text-center font-medium">
            Digite o código <strong>{generatedToken}</strong> de transferência:
          </p>

          <Input.Pin control={control} name="code" inputMode="numeric" />
          <p className="text-red-500">{errors.code?.message}</p>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-primary text-center font-bold">ESTÁ CIENTE DISSO E DESEJA CONTINUAR?</p>

          <div className="flex flex-wrap gap-4">
            <Button onClick={dismiss} color="red" className={button()}>
              Não
            </Button>

            <Button type="submit" color="green" className={button()}>
              Sim
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}

const button = cva({
  base: "flex-1 font-bold uppercase"
});

const logo = cva({
  base: "flex h-20 w-30 place-items-center rounded-md border-2 border-gray-400 p-2"
});

const icon = cva({
  base: "text-primary size-8 rounded-sm border-2 border-gray-400 p-0.5"
});
